import { ArrowLeft as IconArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import YourGrades from '@/components/YourGrades'
import ImproveGrades from '@/components/ImproveGrades'
import ExtraPoints from '@/components/ExtraPoints'
import AddCourse from '@/components/AddCourse'
import useSaveState from '@/hooks/useSaveState'
import { useCalculatorStore } from '@/store/calculatorStore'
import StudyProgramSelector from '@/components/StudyProgramSelector'
import ScoreCard from './components/ScoreCard'
import ChooseHighSchool from './components/ChooseHighSchool'
import ScoreDrawer from './components/ScoreDrawer'
import { useIsDesktop } from './hooks/useMediaQuery'
import { Course } from './data/models'
import coursesList from './data/coursesList'

function updateOldCoursesToNew(oldCourses: Course[]) {
  const newCourses = coursesList.map((course) => {
    const oldCourse = oldCourses.find((c) => c.id === course.id)
    if (oldCourse) {
      if (
        oldCourse.grade !== 0 ||
        oldCourse.exam !== 0 ||
        oldCourse.exam2 !== 0 ||
        oldCourse.improved !== 0 ||
        oldCourse.improved2 !== 0
      ) {
        course.included = true
        course.grade = oldCourse.grade
        course.exam = oldCourse.exam
        course.exam2 = oldCourse.exam2
        course.improved = oldCourse.improved
        course.improved2 = oldCourse.improved2
        course.hadExam = oldCourse.hadExam
        course.numExams = oldCourse.numExams
      }
      return course
    }
    return course
  })

  return newCourses
}

function App() {
  useSaveState()

  const setData = useCalculatorStore((state) => state.setData)
  const status = useCalculatorStore((state) => state.status)
  const uuid = new URLSearchParams(window.location.search).get('uuid')

  useEffect(() => {
    if (!uuid) return
    if (status !== 'new') return

    const fetchData = async () => {
      return Sentry.startSpan(
        {
          op: 'http.client',
          name: `GET /anon-api/users/calculator/${uuid}`,
        },
        async (span) => {
          span.setAttribute('uuid', uuid)

          try {
            const response = await fetch(
              `https://elev.asku.no/anon-api/users/calculator/${uuid}`
            )

            span.setAttribute('response_status', response.status)

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result = await response.json()

            if (result?.studentCalculator.version === '1') {
              const newCourses = updateOldCoursesToNew(
                result?.studentCalculator.calculatorState.courses
              )
              result.studentCalculator.calculatorState.courses = newCourses
              result.studentCalculator.version = '2'
              result.studentCalculator.calculatorState.inputDiploma = 'SSREAL'
              result.studentCalculator.calculatorState.improvedDiploma = 'SSREAL'
              span.setAttribute('version_upgraded', true)
            }

            const dbData = result?.studentCalculator.calculatorState ?? {}
            setData(dbData)
            span.setAttribute('success', true)
          } catch (error) {
            span.setAttribute('success', false)
            Sentry.captureException(error, {
              tags: {
                component: 'App',
                action: 'fetchCalculatorData',
              },
              extra: {
                uuid,
              },
            })
          }
        }
      )
    }

    fetchData()
  }, [uuid, status, setData])

  const [showImproveGrades, setShowImproveGrades] = useState(false)
  const isDesktop = useIsDesktop()

  return (
    <div className="bg-white min-h-screen pb-28 min-[1100px]:pb-4">
      <div className="w-full max-w-[640px] min-[1100px]:max-w-[1280px] min-[1100px]:px-10 mx-auto">
        <div className="min-[1100px]:grid min-[1100px]:grid-cols-[1fr_350px] gap-18">
          <div className="@container">
            {showImproveGrades ? (
              <ImproveGrades onBack={() => setShowImproveGrades(false)} />
            ) : (
              <YourGrades />
            )}
            <div className="@container mt-6 mx-5 min-[1100px]:mx-0">
              <div className="relative @max-[700px]:bg-white @max-[700px]:shadow-[0_2px_4px_0_rgba(0,0,0,0.1)] @max-[700px]:rounded-md @max-[700px]:border @max-[700px]:border-warm-gray-600 @min-[700px]:grid @min-[700px]:grid-cols-[1fr_auto_1fr] @min-[700px]:items-center @min-[700px]:gap-3">
                <AddCourse />
                <span className="pointer-events-none block bg-white px-2 text-[10px] tracking-wider uppercase text-warm-gray-800 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 @min-[700px]:relative @min-[700px]:top-auto @min-[700px]:left-auto @min-[700px]:translate-x-0 @min-[700px]:translate-y-0 @min-[700px]:text-xs">
                  eller
                </span>
                <ChooseHighSchool />
              </div>
            </div>
            <div className="mx-5 min-[1100px]:mx-0">
              <ExtraPoints />
              {!showImproveGrades ? (
                <button
                  className="bg-purple-100 text-purple-800 font-bold text-xl text-center w-full block rounded-lg py-8 mt-14 text-balance cursor-pointer hover:bg-purple-200"
                  onClick={() => {
                    setShowImproveGrades(!showImproveGrades)
                  }}
                >
                  Se hvordan du kan forbedre dine konkurransepoeng
                </button>
              ) : (
                <button
                  onClick={() => setShowImproveGrades(false)}
                  className="text-sm h-8 rounded-full bg-purple-50 border-purple-300 border flex items-center gap-2 px-3 cursor-pointer hover:bg-purple-100 mt-6"
                >
                  <IconArrowLeft className="size-4" />
                  Tilbake til dine karakterer
                </button>
              )}
              <div className="mt-10 min-[1100px]:hidden">
                <StudyProgramSelector />
              </div>
            </div>
          </div>
          <div className="hidden min-[1100px]:block">
            <div className="sticky top-28">
              <ScoreCard showImproveGrades={showImproveGrades} />
              {!showImproveGrades && (
                <button
                  className="w-full text-sm text-center mt-7 cursor-pointer text-purple-800 bg-purple-100 px-4 py-3 rounded-md hover:bg-purple-200"
                  onClick={() => {
                    setShowImproveGrades(!showImproveGrades)
                  }}
                >
                  Se hvordan du kan forbedre dine konkurransepoeng
                </button>
              )}
            </div>
          </div>
          {!isDesktop && (
            <div className="fixed bottom-0 left-0 right-0 pt-10">
              <ScoreDrawer showImproveGrades={showImproveGrades} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default App
